import { useEffect, useMemo, useState } from 'react';
/* eslint-disable react-hooks/exhaustive-deps -- reservation loader is intentionally keyed by branch */
import { supabase } from '../../lib/supabase';
import { useSucursal } from '../../utils/sucursalContext';
import AdminLayout from '../../components/layout/AdminLayout';

type Reserva = {
  id: string;
  usuario: string;
  email: string;
  clase: string;
  fecha: string;
  horario: string;
  hora: string;
  estado: string;
  origen: string;
  fechaReserva: string;
};

type Feriado = {
  id: string;
  fecha: string;
  nombre: string;
  tipo: string;
};

type UsuarioOption = {
  id: string;
  nombre: string;
  email: string;
};

export default function ReservasPage() {
  const { sucursalActiva } = useSucursal();
  const [reservas, setReservas] = useState<Reserva[]>([]);
  const [feriados, setFeriados] = useState<Feriado[]>([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroFecha, setFiltroFecha] = useState('todas');
  const [mostrarModalManual, setMostrarModalManual] = useState(false);
  const [usuariosOpciones, setUsuariosOpciones] = useState<UsuarioOption[]>([]);
  const [busquedaUsuarioModal, setBusquedaUsuarioModal] = useState('');
  const [usuarioIdSeleccionado, setUsuarioIdSeleccionado] = useState('');
  const [tipoAjuste, setTipoAjuste] = useState<'descontar' | 'sumar'>('descontar');
  const [cantidadAjuste, setCantidadAjuste] = useState(1);
  const [motivoAjuste, setMotivoAjuste] = useState('');
  const [guardandoManual, setGuardandoManual] = useState(false);

  useEffect(() => {
    cargarTodo();
  }, [sucursalActiva?.id]);

  const cargarTodo = async () => {
    if (!sucursalActiva?.id) {
      setReservas([]);
      setFeriados([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const [{ data: horarios, error: horariosError }, { data: feriadosData, error: feriadosError }] = await Promise.all([
        supabase
          .from('horarios')
          .select('id, nombre_clase, hora_inicio, hora_fin')
          .eq('sucursal_id', sucursalActiva.id),
        supabase
          .from('feriados')
          .select('id, fecha, nombre, tipo')
          .eq('sucursal_id', sucursalActiva.id)
          .eq('activo', true)
          .gte('fecha', new Date().toISOString().split('T')[0])
          .order('fecha', { ascending: true }),
      ]);

      if (horariosError) throw horariosError;
      if (feriadosError) throw feriadosError;

      const horarioMap = new Map((horarios || []).map((h: any) => [h.id, h]));
      const horarioIds = (horarios || []).map((h: any) => h.id);

      setFeriados(feriadosData || []);

      if (horarioIds.length === 0) {
        setReservas([]);
        return;
      }

      const { data: reservasRaw, error: reservasError } = await supabase
        .from('reservas')
        .select('id, fecha_reserva, hora, estado, origen, created_at, usuario_id, horario_id')
        .in('horario_id', horarioIds)
        .order('fecha_reserva', { ascending: false });

      if (reservasError) throw reservasError;

      const usuarioIds = Array.from(new Set((reservasRaw || []).map((r: any) => r.usuario_id).filter(Boolean)));
      const { data: usuarios, error: usuariosError } = await supabase
        .from('usuarios')
        .select('id, nombre, email')
        .in('id', usuarioIds);

      if (usuariosError) throw usuariosError;

      const usuariosMap = new Map((usuarios || []).map((u: any) => [u.id, u]));

      const parsed: Reserva[] = (reservasRaw || []).map((r: any) => {
        const usuario = usuariosMap.get(r.usuario_id);
        const horario = horarioMap.get(r.horario_id);
        return {
          id: r.id,
          usuario: usuario?.nombre || 'Usuario desconocido',
          email: usuario?.email || '-',
          clase: horario?.nombre_clase || 'Clase',
          fecha: r.fecha_reserva,
          horario: horario ? `${horario.hora_inicio} - ${horario.hora_fin}` : '-',
          hora: r.hora ? String(r.hora).slice(0, 5) : '-',
          estado: r.estado,
          origen: r.origen || 'app',
          fechaReserva: r.created_at,
        };
      });

      setReservas(parsed);
    } catch (error) {
      console.error('Error cargando reservas:', error);
      setReservas([]);
      setFeriados([]);
    } finally {
      setLoading(false);
    }
  };

  const cargarDatosRegistroManual = async () => {
    if (!sucursalActiva?.id) return;

    const { data: usuariosData, error: usuariosError } = await supabase
      .from('usuarios')
      .select('id, nombre, email')
      .eq('sucursal_id', sucursalActiva.id)
      .eq('estado', 'activo')
      .order('nombre', { ascending: true });

    if (usuariosError) throw usuariosError;

    setUsuariosOpciones((usuariosData || []) as UsuarioOption[]);
  };

  const reservasFiltradas = useMemo(() => {
    return reservas.filter((r) => {
      const bySearch =
        r.usuario.toLowerCase().includes(busqueda.toLowerCase()) ||
        r.email.toLowerCase().includes(busqueda.toLowerCase()) ||
        r.clase.toLowerCase().includes(busqueda.toLowerCase());
      const byEstado = filtroEstado === 'todos' || r.estado.toLowerCase() === filtroEstado;
      const byFecha = filtroFecha === 'todas' || r.fecha === filtroFecha;
      return bySearch && byEstado && byFecha;
    });
  }, [reservas, busqueda, filtroEstado, filtroFecha]);

  const fechasDisponibles = useMemo(() => {
    return Array.from(new Set(reservas.map((r) => r.fecha))).sort().reverse();
  }, [reservas]);

  const stats = {
    totalReservas: reservas.length,
    confirmadas: reservas.filter((r) => r.estado === 'confirmada').length,
    pendientes: reservas.filter((r) => r.estado === 'pendiente').length,
    canceladas: reservas.filter((r) => r.estado === 'cancelada').length,
  };

  const cancelarReserva = async (id: string) => {
    if (!confirm('¿Cancelar esta reserva?')) return;

    const { error } = await supabase.rpc('cancelar_reserva', { reserva_id: id });
    if (error) {
      alert(error.message || 'No se pudo cancelar la reserva');
      return;
    }

    setReservas((prev) => prev.map((r) => (r.id === id ? { ...r, estado: 'cancelada' } : r)));
  };

  const abrirModalRegistroManual = async () => {
    if (!sucursalActiva?.id) {
      alert('Debes seleccionar una sucursal activa.');
      return;
    }
    try {
      await cargarDatosRegistroManual();
      setBusquedaUsuarioModal('');
      setUsuarioIdSeleccionado('');
      setTipoAjuste('descontar');
      setCantidadAjuste(1);
      setMotivoAjuste('');
      setMostrarModalManual(true);
    } catch (error: any) {
      alert(error?.message || 'No se pudieron cargar datos para el registro manual');
    }
  };

  const usuariosFiltradosModal = useMemo(() => {
    const q = busquedaUsuarioModal.trim().toLowerCase();
    if (!q) return usuariosOpciones.slice(0, 80);
    return usuariosOpciones
      .filter((u) => u.nombre.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
      .slice(0, 80);
  }, [usuariosOpciones, busquedaUsuarioModal]);

  const registrarAsistenciaManual = async () => {
    if (!usuarioIdSeleccionado) {
      alert('Selecciona un usuario.');
      return;
    }
    if (!Number.isFinite(cantidadAjuste) || cantidadAjuste <= 0) {
      alert('Ingresa una cantidad válida mayor a 0.');
      return;
    }

    const delta = tipoAjuste === 'sumar' ? cantidadAjuste : -cantidadAjuste;

    try {
      setGuardandoManual(true);
      const { error } = await supabase.rpc('ajustar_sesiones_usuario_manual', {
        p_usuario_id: usuarioIdSeleccionado,
        p_delta: delta,
        p_motivo: motivoAjuste.trim() || null,
      });

      if (error) throw error;

      alert(
        tipoAjuste === 'sumar'
          ? `Se sumaron ${cantidadAjuste} sesión(es) correctamente.`
          : `Se descontaron ${cantidadAjuste} sesión(es) correctamente.`,
      );
      setMostrarModalManual(false);
      await cargarTodo();
    } catch (error: any) {
      alert(error?.message || 'No se pudo registrar el ajuste manual');
    } finally {
      setGuardandoManual(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-4 md:space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reservas</h1>
            <p className="text-sm text-gray-500 mt-1">Gestión real de reservas por sucursal</p>
          </div>
          <button
            onClick={abrirModalRegistroManual}
            className="w-full sm:w-auto px-4 py-2.5 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700"
          >
            <i className="ri-user-add-line mr-1"></i>
            Registrar asistencia manual
          </button>
        </div>

        {feriados.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-amber-800 mb-2">Próximos días sin servicio</h4>
            <div className="flex flex-wrap gap-2">
              {feriados.slice(0, 6).map((f) => (
                <span key={f.id} className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                  {new Date(`${f.fecha}T12:00:00`).toLocaleDateString('es-CL', { day: '2-digit', month: 'short' })} - {f.nombre}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          <StatCard title="Total" value={stats.totalReservas} icon="ri-bookmark-line" />
          <StatCard title="Confirmadas" value={stats.confirmadas} icon="ri-check-line" />
          <StatCard title="Pendientes" value={stats.pendientes} icon="ri-time-line" />
          <StatCard title="Canceladas" value={stats.canceladas} icon="ri-close-circle-line" />
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar usuario, email o clase"
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="todos">Todos los estados</option>
            <option value="confirmada">Confirmada</option>
            <option value="pendiente">Pendiente</option>
            <option value="cancelada">Cancelada</option>
          </select>
          <select
            value={filtroFecha}
            onChange={(e) => setFiltroFecha(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="todas">Todas las fechas</option>
            {fechasDisponibles.map((fecha) => (
              <option key={fecha} value={fecha}>
                {new Date(`${fecha}T12:00:00`).toLocaleDateString('es-CL')}
              </option>
            ))}
          </select>
          <button onClick={cargarTodo} className="px-3 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700">
            Actualizar
          </button>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[850px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Usuario</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Clase</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Fecha</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Horario</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Estado</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {reservasFiltradas.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-500">
                      No se encontraron reservas con los filtros aplicados
                    </td>
                  </tr>
                ) : (
                  reservasFiltradas.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{r.usuario}</p>
                          <p className="text-xs text-gray-500">{r.email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{r.clase}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {new Date(`${r.fecha}T12:00:00`).toLocaleDateString('es-CL')}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{r.horario}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                              r.estado === 'confirmada'
                                ? 'bg-green-100 text-green-700'
                                : r.estado === 'pendiente'
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {r.estado}
                          </span>
                          {r.origen === 'manual_monitor' && (
                            <span className="inline-flex px-2 py-1 rounded-full text-[11px] font-medium bg-blue-100 text-blue-700">
                              Manual monitor
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        {r.estado !== 'cancelada' && (
                          <button
                            onClick={() => cancelarReserva(r.id)}
                            className="px-3 py-1.5 text-xs bg-red-50 text-red-600 rounded-lg hover:bg-red-100"
                          >
                            Cancelar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {mostrarModalManual && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-3xl max-h-[92dvh] overflow-y-auto bg-white rounded-xl shadow-xl border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Registrar asistencia manual</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Ajusta sesiones de un usuario sin usar fecha ni bloque horario.</p>
                </div>
                <button
                  onClick={() => setMostrarModalManual(false)}
                  className="w-9 h-9 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Operación</label>
                    <select
                      value={tipoAjuste}
                      onChange={(e) => setTipoAjuste(e.target.value as 'descontar' | 'sumar')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="descontar">Descontar sesiones</option>
                      <option value="sumar">Sumar sesiones</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Cantidad</label>
                    <input
                      type="number"
                      min={1}
                      step={1}
                      value={cantidadAjuste}
                      onChange={(e) => setCantidadAjuste(Math.max(1, Number(e.target.value || 1)))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    {[1, 2, 4].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setCantidadAjuste(n)}
                        className={`px-3 py-2 rounded-lg text-sm border ${
                          cantidadAjuste === n
                            ? 'bg-amber-50 border-amber-400 text-amber-700'
                            : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Buscar usuario</label>
                  <input
                    type="text"
                    value={busquedaUsuarioModal}
                    onChange={(e) => setBusquedaUsuarioModal(e.target.value)}
                    placeholder="Nombre o email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Motivo (opcional)</label>
                  <input
                    type="text"
                    value={motivoAjuste}
                    onChange={(e) => setMotivoAjuste(e.target.value)}
                    placeholder="Ej: asistencia manual semanal, corrección de sesiones"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>

                <div className="max-h-72 overflow-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                  {usuariosFiltradosModal.length === 0 ? (
                    <div className="p-4 text-sm text-gray-500">No hay usuarios activos para mostrar.</div>
                  ) : (
                    usuariosFiltradosModal.map((u) => (
                      <button
                        key={u.id}
                        onClick={() => setUsuarioIdSeleccionado(u.id)}
                        className={`w-full text-left px-4 py-3 hover:bg-gray-50 ${
                          usuarioIdSeleccionado === u.id ? 'bg-amber-50 border-l-4 border-amber-500' : ''
                        }`}
                      >
                        <p className="text-sm font-medium text-gray-900">{u.nombre}</p>
                        <p className="text-xs text-gray-500">{u.email}</p>
                      </button>
                    ))
                  )}
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-2">
                <button
                  onClick={() => setMostrarModalManual(false)}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={registrarAsistenciaManual}
                  disabled={guardandoManual}
                  className="px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-60"
                >
                  {guardandoManual ? 'Registrando...' : 'Registrar asistencia'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function StatCard({ title, value, icon }: { title: string; value: number; icon: string }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
        <div className="w-10 h-10 bg-teal-50 text-teal-600 rounded-lg flex items-center justify-center">
          <i className={`${icon} text-lg`}></i>
        </div>
      </div>
    </div>
  );
}
